import React from 'react';
import { Link } from 'react-router-dom';
import { Switch } from 'antd';
import { Check, Eye } from 'lucide-react';
import { formatMinutes } from './applicationProgress';

const PHASES = {
    application: { step: 2, title: 'Complete Application' },
    profile: { step: 3, title: 'Build Profile' },
    live: { step: 4, title: 'Live' },
};

/**
 * Slim progress strip shown above the application / profile builder:
 * which journey step they're on, how much is left, and an always-reachable
 * "Save & exit" (plus "Preview client view" in the profile phase).
 *
 * Once the profile is live it switches to a summary (required + optional
 * counts) and can show the "Open to Work" availability toggle.
 *
 * @param {'application'|'profile'|'live'} phase
 * @param {{added: number, total: number}} optional  Optional sections added (live only)
 * @param {{checked: boolean, busy: boolean, onChange: function}} openToWork  Availability toggle (live only)
 */
const ApplicationStatusStrip = ({
    progress,
    phase = 'application',
    optional = null,
    openToWork = null,
    isReady = false,
    previewHref = null,
    onSaveAndExit,
    showSaveAndExit = true,
    disabled = false,
}) => {
    if (!progress) return null;

    const { step, title } = PHASES[phase] || PHASES.application;
    const isLive = phase === 'live';
    const showExit = showSaveAndExit && !!onSaveAndExit;

    return (
        <div className="w-full bg-white border-t border-b border-gray-200">
            <div className="flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-1.5 md:gap-6 px-4 md:px-8 py-2 md:py-3">

                {/* Where am I in the journey */}
                <div className="order-1 min-w-0 grow shrink basis-0 md:flex-none md:shrink-0 md:w-auto">
                    <p className="m-0 text-[10px] font-semibold uppercase tracking-wider text-gray-400 leading-4">
                        Step {step} of 4
                    </p>
                    <p className="m-0 flex flex-wrap items-baseline gap-x-3 text-sm md:text-[15px] font-semibold text-gray-900 leading-5">
                        <span className="truncate">{title}</span>
                        {isLive && (
                            <span className="text-[11px] md:text-xs font-normal text-gray-500 whitespace-nowrap">
                                <b className="text-gray-900 tabular-nums">{progress.requiredDoneCount} of {progress.requiredCount}</b> required
                                {optional?.total > 0 && (
                                    <> &middot; <b className="text-gray-900 tabular-nums">{optional.added} of {optional.total}</b> optional added</>
                                )}
                            </span>
                        )}
                    </p>
                </div>

                {/* How far along (building phases only) */}
                {!isLive && (
                    <div className="order-3 md:order-2 w-full md:w-auto flex md:flex-1 min-w-0 items-center gap-3">
                        <div className="h-1.5 grow shrink basis-0 md:max-w-[280px] overflow-hidden rounded-full bg-gray-200">
                            <div
                                className="h-full rounded-full bg-success transition-all duration-700"
                                style={{ width: `${progress.percent}%` }}
                            />
                        </div>
                        <p className="m-0 whitespace-nowrap text-[11px] md:text-xs text-gray-500">
                            {isReady ? (
                                <span className="inline-flex items-center gap-1 font-semibold text-success">
                                    <Check className="h-3.5 w-3.5" strokeWidth={3} /> Ready to submit
                                </span>
                            ) : (
                                <>
                                    <b className="text-gray-900 tabular-nums">{progress.requiredDoneCount} of {progress.requiredCount}</b> required done
                                    {progress.minutesLeft > 0 && <span className="hidden sm:inline"> &middot; ~{formatMinutes(progress.minutesLeft)} left</span>}
                                </>
                            )}
                        </p>
                    </div>
                )}

                {/* Always-reachable actions */}
                <div className="order-2 md:order-3 md:ml-auto flex shrink-0 items-center gap-3 md:gap-4 text-xs md:text-sm font-semibold">
                    {openToWork && (
                        <label className="m-0 inline-flex items-center gap-2 text-gray-900 cursor-pointer whitespace-nowrap">
                            Open to Work
                            <Switch
                                size="small"
                                checked={!!openToWork.checked}
                                loading={!!openToWork.busy}
                                disabled={disabled || openToWork.busy}
                                onChange={openToWork.onChange}
                            />
                        </label>
                    )}
                    {openToWork && (previewHref || showExit) && <span className="hidden md:block h-4 w-px bg-gray-200" />}
                    {previewHref && (
                        // !text-secondary: purple like "Save & exit" (beats the global link colour)
                        <Link to={previewHref} className="hidden md:inline-flex items-center gap-1.5 !text-secondary hover:!text-secondary-dark hover:no-underline">
                            <Eye className="h-4 w-4" /> Preview client view
                        </Link>
                    )}
                    {previewHref && showExit && <span className="hidden md:block h-4 w-px bg-gray-200" />}
                    {showExit && (
                        <button
                            type="button"
                            disabled={disabled}
                            onClick={onSaveAndExit}
                            className="bg-transparent border-0 p-0 text-secondary hover:text-secondary-dark disabled:opacity-50 whitespace-nowrap"
                        >
                            Save &amp; exit
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ApplicationStatusStrip;
